import React, { useState, useEffect } from "react";
import {
  X,
  Calendar,
  Clock,
  Users,
  User,
  AlertCircle,
  CheckCircle,
  Loader,
  Briefcase,
  Tag,
  FileText,
  Flag,
  ListChecks,
  MessageSquare,
  ShieldCheck,
  Plus,
  XCircle,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const CreateTeamTaskModal = ({
  onClose,
  onSuccess,
  preSelectedTeamId = null,
}) => {
  const { isDarkMode } = useTheme();
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [tagInput, setTagInput] = useState("");

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "general",
    priority: "medium",
    dueDate: "",
    startDate: "",
    teamId: preSelectedTeamId || "",
    assignedUserIds: [],
    requiresResponse: false,
    requiresApproval: false,
    tags: [],
  });

  useEffect(() => {
    fetchTeams();

    // Set default start date to today and due date to tomorrow
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    setFormData((prev) => ({
      ...prev,
      startDate: today.toISOString().slice(0, 16),
      dueDate: tomorrow.toISOString().slice(0, 16),
    }));
  }, []);

  useEffect(() => {
    if (formData.teamId) {
      fetchTeamMembers(formData.teamId);
    } else {
      setTeamMembers([]);
      setFormData((prev) => ({ ...prev, assignedUserIds: [] }));
    }
  }, [formData.teamId]);

  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const response = await apiService.getTeams();
      if (response.success) {
        setTeams(response.data || response.teams || []);
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
      setError("Failed to load teams");
    } finally {
      setLoadingTeams(false);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      setLoadingMembers(true);
      const response = await apiService.getTeamMembers(teamId);
      if (response.success) {
        const members = response.data || response.members || [];
        setTeamMembers(members);
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleAssigneeToggle = (userId) => {
    setFormData((prev) => {
      const current = prev.assignedUserIds;
      if (current.includes(userId)) {
        return {
          ...prev,
          assignedUserIds: current.filter((id) => id !== userId),
        };
      }
      return { ...prev, assignedUserIds: [...current, userId] };
    });
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      if (!formData.tags.includes(tagInput.trim())) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, tagInput.trim()],
        }));
      }
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.title.trim()) {
      setError("Task title is required");
      setLoading(false);
      return;
    }

    if (!formData.teamId) {
      setError("Please select a team");
      setLoading(false);
      return;
    }

    if (formData.assignedUserIds.length === 0) {
      setError("Please assign at least one team member");
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        dueDate: formData.dueDate
          ? new Date(formData.dueDate).toISOString()
          : null,
        startDate: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : null,
      };

      const response = await apiService.createTeamTask(payload);

      if (response.success) {
        onSuccess(response);
      } else {
        setError(response.message || "Failed to create team task");
      }
    } catch (err) {
      console.error("Error creating team task:", err);
      setError(
        err.response?.data?.message ||
          "Failed to create team task. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const getMemberName = (member) => {
    return member?.user?.name || member?.name || "Unknown";
  };

  const getMemberEmail = (member) => {
    return member?.user?.email || member?.email || "";
  };

  const getMemberId = (member) => {
    return member?.user?.id || member?.userId || member?.id;
  };

  const getMemberImage = (member) => {
    return member?.user?.profileImageUrl || member?.profileImageUrl || null;
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div
        className={`rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden ${
          isDarkMode
            ? "bg-gradient-to-br from-[#0F1115] to-[#0A0C10] border border-gray-800/50"
            : "bg-gradient-to-br from-white to-gray-50 border border-gray-200"
        }`}
      >
        {/* Header */}
        <div
          className={`sticky top-0 z-10 border-b p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#13161B] to-[#0F1115]"
              : "border-gray-200 bg-gradient-to-br from-white to-gray-50"
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-lg ${
                  isDarkMode
                    ? "bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-600/30"
                    : "bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-500/20"
                }`}
              >
                <ListChecks size={20} className="text-white" />
              </div>
              <div>
                <h2
                  className={`text-xl font-bold ${
                    isDarkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Create Team Task
                </h2>
                <p
                  className={`text-sm ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  Assign a task to your team members
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-xl transition-all duration-200 ${
                isDarkMode
                  ? "hover:bg-gray-800/50 text-gray-400 hover:text-white"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-6">
          {error && (
            <div
              className={`mb-4 p-4 rounded-xl flex items-center gap-3 ${
                isDarkMode
                  ? "bg-red-500/10 border border-red-500/50"
                  : "bg-red-50 border border-red-200"
              }`}
            >
              <AlertCircle
                size={20}
                className={`flex-shrink-0 ${
                  isDarkMode ? "text-red-400" : "text-red-500"
                }`}
              />
              <p
                className={`text-sm ${
                  isDarkMode ? "text-red-200" : "text-red-600"
                }`}
              >
                {error}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <FileText size={14} className="inline mr-1" />
                Task Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                  isDarkMode
                    ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Enter task title..."
                required
              />
            </div>

            {/* Description */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 resize-none ${
                  isDarkMode
                    ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Describe the task details, requirements, and expectations..."
              />
            </div>

            {/* Type & Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <Briefcase size={14} className="inline mr-1" />
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="general">General</option>
                  <option value="urgent">Urgent</option>
                  <option value="assignment">Assignment</option>
                  <option value="request">Request</option>
                  <option value="approval">Approval</option>
                  <option value="notification">Notification</option>
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <Flag size={14} className="inline mr-1" />
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Start & Due Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <Calendar size={14} className="inline mr-1" />
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) =>
                    setFormData({ ...formData, startDate: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <Clock size={14} className="inline mr-1" />
                  Due Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.dueDate}
                  onChange={(e) =>
                    setFormData({ ...formData, dueDate: e.target.value })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>
            </div>

            {/* Team Selection */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <Users size={14} className="inline mr-1" />
                Team
              </label>
              {loadingTeams ? (
                <div
                  className={`w-full px-4 py-3 border rounded-xl flex items-center justify-center ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <Loader
                    className={`animate-spin ${
                      isDarkMode ? "text-blue-400" : "text-blue-500"
                    }`}
                    size={18}
                  />
                  <span
                    className={`ml-2 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    Loading teams...
                  </span>
                </div>
              ) : (
                <select
                  value={formData.teamId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      teamId: e.target.value,
                      assignedUserIds: [],
                    })
                  }
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                    isDarkMode
                      ? "bg-gray-800/50 border-gray-700/50 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                  required
                >
                  <option value="">Select a team...</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Assignees */}
            {formData.teamId && (
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <User size={14} className="inline mr-1" />
                  Assign To
                  {formData.assignedUserIds.length > 0 && (
                    <span className="ml-2 text-xs text-blue-500">
                      ({formData.assignedUserIds.length} selected)
                    </span>
                  )}
                </label>
                {loadingMembers ? (
                  <div
                    className={`w-full px-4 py-3 border rounded-xl flex items-center justify-center ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700/50"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <Loader
                      className={`animate-spin ${
                        isDarkMode ? "text-blue-400" : "text-blue-500"
                      }`}
                      size={18}
                    />
                    <span
                      className={`ml-2 ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      Loading members...
                    </span>
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div
                    className={`w-full px-4 py-3 border rounded-xl text-sm text-center ${
                      isDarkMode
                        ? "bg-gray-800/50 border-gray-700/50 text-gray-500"
                        : "bg-gray-50 border-gray-200 text-gray-500"
                    }`}
                  >
                    No members found in this team
                  </div>
                ) : (
                  <div
                    className={`border rounded-xl p-3 max-h-48 overflow-y-auto space-y-1 ${
                      isDarkMode
                        ? "bg-gray-800/30 border-gray-700/50"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    {teamMembers.map((member) => {
                      const memberId = getMemberId(member);
                      const isSelected =
                        formData.assignedUserIds.includes(memberId);

                      return (
                        <button
                          key={memberId}
                          type="button"
                          onClick={() => handleAssigneeToggle(memberId)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left ${
                            isSelected
                              ? isDarkMode
                                ? "bg-blue-500/15 border border-blue-500/30"
                                : "bg-blue-50 border border-blue-200"
                              : isDarkMode
                                ? "hover:bg-gray-700/30 border border-transparent"
                                : "hover:bg-gray-100 border border-transparent"
                          }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                              isSelected
                                ? "bg-blue-500 border-blue-500"
                                : isDarkMode
                                  ? "border-gray-600 bg-transparent"
                                  : "border-gray-300 bg-transparent"
                            }`}
                          >
                            {isSelected && (
                              <CheckCircle size={12} className="text-white" />
                            )}
                          </div>
                          {getMemberImage(member) ? (
                            <img
                              src={getMemberImage(member)}
                              alt={getMemberName(member)}
                              className="w-8 h-8 rounded-lg object-cover"
                            />
                          ) : (
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                isDarkMode
                                  ? "bg-gradient-to-br from-gray-600 to-gray-700"
                                  : "bg-gradient-to-br from-gray-400 to-gray-500"
                              }`}
                            >
                              <span className="text-white text-xs font-semibold">
                                {getMemberName(member).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-sm font-medium truncate ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              {getMemberName(member)}
                            </div>
                            <div
                              className={`text-xs truncate ${
                                isDarkMode ? "text-gray-500" : "text-gray-500"
                              }`}
                            >
                              {getMemberEmail(member)}
                            </div>
                          </div>
                          {member.role && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${
                                isDarkMode
                                  ? "text-gray-500 bg-gray-800/50"
                                  : "text-gray-500 bg-gray-100"
                              }`}
                            >
                              {member.role}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            <div>
              <label
                className={`block text-sm font-medium mb-2 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                <Tag size={14} className="inline mr-1" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium ${
                      isDarkMode
                        ? "bg-blue-500/15 border border-blue-500/30 text-blue-300"
                        : "bg-blue-100 border border-blue-200 text-blue-700"
                    }`}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className={`transition-colors ${
                        isDarkMode ? "hover:text-red-400" : "hover:text-red-600"
                      }`}
                    >
                      <XCircle size={12} />
                    </button>
                  </span>
                ))}
              </div>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all duration-200 ${
                  isDarkMode
                    ? "bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
                placeholder="Type a tag and press Enter..."
              />
            </div>

            {/* Options */}
            <div
              className={`border rounded-xl p-4 space-y-3 ${
                isDarkMode
                  ? "bg-gray-800/20 border-gray-700/30"
                  : "bg-gray-50 border-gray-200"
              }`}
            >
              <p
                className={`text-sm font-medium mb-1 ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
              >
                Options
              </p>
              <label className={`flex items-center gap-3 cursor-pointer group`}>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.requiresResponse}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requiresResponse: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-10 h-5 rounded-full transition-all duration-200 ${
                      formData.requiresResponse
                        ? "bg-blue-500"
                        : isDarkMode
                          ? "bg-gray-700"
                          : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-200 translate-y-0.5 ${
                        formData.requiresResponse
                          ? "translate-x-5"
                          : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
                <div>
                  <div
                    className={`text-sm transition-colors ${
                      isDarkMode
                        ? "text-white group-hover:text-blue-300"
                        : "text-gray-700 group-hover:text-blue-600"
                    }`}
                  >
                    <MessageSquare size={14} className="inline mr-1" />
                    Requires Response
                  </div>
                  <div
                    className={`text-xs ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Assignees must provide a response when completing
                  </div>
                </div>
              </label>

              <label className={`flex items-center gap-3 cursor-pointer group`}>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        requiresApproval: e.target.checked,
                      })
                    }
                    className="sr-only"
                  />
                  <div
                    className={`w-10 h-5 rounded-full transition-all duration-200 ${
                      formData.requiresApproval
                        ? "bg-blue-500"
                        : isDarkMode
                          ? "bg-gray-700"
                          : "bg-gray-300"
                    }`}
                  >
                    <div
                      className={`w-4 h-4 rounded-full bg-white shadow-md transform transition-transform duration-200 translate-y-0.5 ${
                        formData.requiresApproval
                          ? "translate-x-5"
                          : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
                <div>
                  <div
                    className={`text-sm transition-colors ${
                      isDarkMode
                        ? "text-white group-hover:text-blue-300"
                        : "text-gray-700 group-hover:text-blue-600"
                    }`}
                  >
                    <ShieldCheck size={14} className="inline mr-1" />
                    Requires Approval
                  </div>
                  <div
                    className={`text-xs ${
                      isDarkMode ? "text-gray-500" : "text-gray-500"
                    }`}
                  >
                    Task completion needs your approval before closing
                  </div>
                </div>
              </label>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div
          className={`border-t p-6 ${
            isDarkMode
              ? "border-gray-800/50 bg-gradient-to-br from-[#0C0E12] to-[#0A0C10]"
              : "border-gray-200 bg-gradient-to-br from-gray-50 to-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-5 py-3 border rounded-xl font-medium transition-all duration-200 ${
                isDarkMode
                  ? "bg-gray-800/50 hover:bg-gray-800 border-gray-700/50 hover:border-gray-600/50 text-gray-300 hover:text-white"
                  : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700 hover:text-gray-900"
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || loadingTeams}
              className="flex-1 px-5 py-3 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-600/20 hover:shadow-blue-600/30"
              style={{
                background: isDarkMode
                  ? "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)"
                  : "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              }}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Plus size={18} />
                  <span>Create Task</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CreateTeamTaskModal;
